import { useCallback, useEffect, useState } from 'react'
import { Home } from './pages/Home'
import { Chat } from './pages/Chat'
import { Login } from './pages/Login'
import { api, AuthError, type UserPrefs } from './api'
import { clearSession, getSession } from './auth'

// 路由：login | home | chat。统一在顶层，避免子组件做导航判断。
type Page = 'login' | 'home' | 'chat'

export function App() {
    const [page, setPage] = useState<Page>(() => (getSession() ? 'home' : 'login'))
    const [theme, setTheme] = useState<'light' | 'dark'>('light')
    const [persona, setPersona] = useState<string>('patient')
    // 气泡字号:仅本机偏好,存 localStorage(没接服务端 prefs);默认中档 16
    const [fontSize, setFontSize] = useState<number>(() => {
        const v = Number(localStorage.getItem('sb_font_size'))
        return v >= 12 && v <= 24 ? v : 16
    })
    // 模糊 AI 文字(只听语音):同样仅本机
    const [blurAi, setBlurAi] = useState<boolean>(() => localStorage.getItem('sb_blur_ai') === '1')
    const [bootDone, setBootDone] = useState(false)

    // 启动时若已有 session，验证 token + 拉用户偏好。无效就跳回 login。
    useEffect(() => {
        if (!getSession()) {
            setBootDone(true)
            return
        }
        api.auth
            .me()
            .then((u: UserPrefs) => {
                setTheme(u.theme === 'dark' ? 'dark' : 'light')
                setPersona(u.persona || 'patient')
            })
            .catch((err) => {
                if (err instanceof AuthError) {
                    setPage('login')
                } else {
                    console.warn('[app] load user prefs failed:', err)
                }
            })
            .finally(() => setBootDone(true))
    }, [])

    // theme 应用到 root：CSS 通过 [data-theme="dark"] 读取
    useEffect(() => {
        document.documentElement.dataset.theme = theme
    }, [theme])

    const handleAuthed = useCallback(async () => {
        try {
            const u = await api.auth.me()
            setTheme(u.theme === 'dark' ? 'dark' : 'light')
            setPersona(u.persona || 'patient')
        } catch {
            /* boot 期失败容忍，用默认偏好 */
        }
        setPage('home')
    }, [])

    const changeTheme = useCallback(async (next: 'light' | 'dark') => {
        setTheme(next)
        try {
            await api.user.update({ theme: next })
        } catch (err) {
            if (err instanceof AuthError) {
                setPage('login')
                return
            }
            console.warn('[app] save theme failed:', err)
        }
    }, [])

    const changePersona = useCallback(async (next: string) => {
        setPersona(next)
        try {
            await api.user.update({ persona: next })
        } catch (err) {
            if (err instanceof AuthError) {
                setPage('login')
                return
            }
            console.warn('[app] save persona failed:', err)
        }
    }, [])

    const changeFontSize = useCallback((px: number) => {
        setFontSize(px)
        localStorage.setItem('sb_font_size', String(px))
    }, [])

    const toggleBlurAi = useCallback(() => {
        setBlurAi((b) => {
            const next = !b
            localStorage.setItem('sb_blur_ai', next ? '1' : '0')
            return next
        })
    }, [])

    const logout = useCallback(() => {
        clearSession()
        setPage('login')
    }, [])

    // boot 期间空白，避免页面闪烁（先 home 后被 401 踢回 login 体验差）
    if (!bootDone) return null

    if (page === 'login') {
        return <Login onAuthed={handleAuthed} />
    }
    if (page === 'home') {
        return (
            <Home theme={theme} onChangeTheme={changeTheme} onStart={() => setPage('chat')} onLogout={logout} />
        )
    }
    return (
        <Chat
            theme={theme}
            onChangeTheme={changeTheme}
            persona={persona}
            onChangePersona={changePersona}
            fontSize={fontSize}
            onChangeFontSize={changeFontSize}
            blurAi={blurAi}
            onToggleBlurAi={toggleBlurAi}
            onBack={() => setPage('home')}
            onLogout={logout}
        />
    )
}
